var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import { useQuery } from "../../hooks";
import { ETeam, TeamName, TMatchResultCase, } from "../../types";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import * as api from "../../api/matches";
const defaultAvatar = "img/defaultAvatar2.png";
const drawBackground = "img/finalViewImages/draw.png";
const anarchistBackground = "img/finalViewImages/win_anarchist.png";
const civiliansBackground = "img/finalViewImages/win_civilians.png";
const mafiaBackground = "img/finalViewImages/win_mafia.png";
const werewolfBackground = "img/finalViewImages/win_werewolf.png";
const Section = styled.section `
  position: relative;
  overflow: hidden;
  background-color: #0c0d17;
  min-height: 100vh;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to bottom,
        rgba(12, 13, 23, 0.5) 10%,
        rgba(12, 13, 23, 1) 40%
      ),
      url(${({ backgroundUrl }) => backgroundUrl}) no-repeat center top;
    background-size: contain;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;
const BackgroundEffect = styled.div `
  width: 558px;
  height: 558px;
  position: fixed;
  bottom: 0;
  right: 0;
  background-color: #8257264d;
  border-radius: 50%;
  transform: translate(50%, 50%);
  filter: blur(200px);
  z-index: 0;
`;
const HeadingContainer = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  margin-bottom: 14px;
`;
const Title = styled(Typography) `
  font-family: LosAngelesDeco, Roboto, serif !important;
  font-weight: 400 !important;
  font-size: 64px !important;
  line-height: 1 !important;
  text-transform: uppercase;
  color: #d4a55d;
  text-align: left;
  background: linear-gradient(
    90deg,
    rgba(255, 183, 102, 0.5) 0%,
    rgba(255, 183, 102, 1) 50%,
    rgba(255, 183, 102, 0.5) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 20px 0 0 !important;
`;
const Date = styled(Typography) `
  font-family: GreatGatsbyTwo, Roboto, serif !important;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 1 !important;
  text-align: center;
  background: linear-gradient(
    90deg,
    rgba(255, 183, 102, 0.5) 0%,
    rgba(255, 183, 102, 1) 50%,
    rgba(255, 183, 102, 0.5) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 12px 0 !important;
`;
const DateContainer = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
`;
const RolePostfix = styled(Typography) `
  font-family: GreatGatsbyTwo, Roboto, serif !important;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 1 !important;
  text-transform: lowercase;
  text-align: center;
  background: linear-gradient(
    90deg,
    rgba(255, 183, 102, 0.5) 0%,
    rgba(255, 183, 102, 1) 50%,
    rgba(255, 183, 102, 0.5) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 12px 0 !important;
  display: flex;
  align-items: center;
  gap: 8px;

  &:after {
    content: "";
    display: block;
    width: 4px;
    height: 4px;
    background-color: #8c6845;
    border-radius: 50%;
  }
`;
const StatusLegend = styled.div `
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;
const LegendItem = styled.div `
  font-family: FuturaPT, Roboto, serif !important;
  display: flex;
  align-items: center;
  gap: 6px;
  color: white;
  font-size: 20px;
  font-weight: 450;
`;
const Circle = styled.span `
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${({ circleColor }) => circleColor};
  box-shadow: 0 0 4px ${({ circleColor }) => circleColor},
    inset 0 0 4px ${({ circleColor }) => circleColor};
  filter: blur(0.3px);
`;
const TeamContainer = styled.div `
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const TeamTitle = styled.h4 `
  font-family: LosAngelesDeco, Roboto, serif !important;
  font-weight: 400;
  font-size: 36px;
  line-height: 1;
  text-transform: uppercase;
  background: linear-gradient(
    90deg,
    rgba(255, 183, 102, 0.5) 0%,
    rgba(255, 183, 102, 1) 50%,
    rgba(255, 183, 102, 0.5) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 auto;
`;
const Teams = styled.div `
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  padding: 0 8px 12px;
`;
const Cards = styled.div `
  display: flex;
  flex-direction: column;
  max-width: 400px;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(3px);
`;
const Card = styled.div `
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;
const PlayerStatus = styled.div `
  padding: 2px;
  font-size: 0;
  border-radius: 50%;
  border: 1px solid ${({ borderColor }) => borderColor};
  box-shadow: 0 0 6px ${({ borderColor }) => borderColor},
    inset 0 0 4px ${({ borderColor }) => borderColor};
`;
const PlayerPic = styled.img `
  height: 32px;
  width: 32px;
  min-width: 32px;
  border-radius: 50%;
  object-fit: contain;
`;
const KillReason = styled(Typography) `
  font-family: FuturaPT, Roboto, serif !important;
  color: #b9bbc5 !important;
  font-size: 12px;
  line-height: 1 !important;
  font-weight: 450;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px !important;

  &:before {
    content: "";
    display: block;
    width: 4px;
    height: 4px;
    background-color: #b9bbc5;
    border-radius: 50%;
  }
`;
const PlayerRate = styled.div `
  color: #fff;
  font-family: FuturaPT, Roboto, serif !important;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  border-radius: 50px;
  padding: 6px;
  border: 1px solid #ffc788;
  box-shadow: 0 0 6px #ffc788, inset 0 0 4px #ffc788;
`;
const PlayerName = styled.p `
  color: #fff;
  font-family: FuturaPT, Roboto, serif !important;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  margin: 0 0 0 10px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const PlayerRole = styled.p `
  font-family: FuturaPT, Roboto, serif !important;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #fff;
  margin: 0 12px 0 auto;
  text-align: right;
  white-space: nowrap;
`;
const getWinnerString = (result) => {
    switch (result) {
        case TMatchResultCase.MAFIA_WIN:
            return "Мафия победила";
        case TMatchResultCase.CIVILIANS_WIN:
            return "Мирные победили";
        case TMatchResultCase.ANARCHIST_WIN:
            return "Победил анархист";
        case TMatchResultCase.WEREWOLF_WIN:
            return "Оборотень";
        default:
            return "Ничья";
    }
};
const getBackground = (result) => {
    switch (result) {
        case TMatchResultCase.MAFIA_WIN:
            return mafiaBackground;
        case TMatchResultCase.CIVILIANS_WIN:
            return civiliansBackground;
        case TMatchResultCase.ANARCHIST_WIN:
            return anarchistBackground;
        case TMatchResultCase.WEREWOLF_WIN:
            return werewolfBackground;
        default:
            return drawBackground;
    }
};
const getTeamsList = (winCase) => {
    // eslint-disable-next-line default-case
    switch (winCase) {
        case TMatchResultCase.FOOLS_WIN:
            return [ETeam.FOOLS, ETeam.MAFIA, ETeam.CIVILIANS];
        case TMatchResultCase.CIVILIANS_WIN:
            return [ETeam.CIVILIANS, ETeam.MAFIA, ETeam.FOOLS];
        case TMatchResultCase.MAFIA_WIN:
            return [ETeam.MAFIA, ETeam.FOOLS, ETeam.CIVILIANS];
    }
    return [ETeam.CIVILIANS, ETeam.MAFIA, ETeam.FOOLS];
};
const getName = (player) => {
    if (player.gameName) {
        return player.gameName;
    }
    return player.realName;
};
export function DirectLinkFinalTable() {
    var _a, _b, _c, _d;
    const { param: matchId } = useQuery("matchId");
    const { param: accessToken } = useQuery("accessToken");
    const { errorHandler } = useErrorHandler();
    const [finalDataState, setFinalDataState] = useState(null);
    const getFinalData = () => __awaiter(this, void 0, void 0, function* () {
        try {
            const result = yield api.getMatchFinalData(matchId, accessToken);
            setFinalDataState(result);
        }
        catch (e) {
            console.error("Match getMatchFinalData", e.response);
            errorHandler(e);
        }
    });
    const allGamePlayers = useMemo(() => ((finalDataState === null || finalDataState === void 0 ? void 0 : finalDataState.state.players) || []), [finalDataState]);
    const playersInTeam = (team) => allGamePlayers === null || allGamePlayers === void 0 ? void 0 : allGamePlayers.filter((p) => p.team === team);
    const event = ((_a = finalDataState === null || finalDataState === void 0 ? void 0 : finalDataState.state) === null || _a === void 0 ? void 0 : _a.event) || null;
    const winCase = (_b = finalDataState === null || finalDataState === void 0 ? void 0 : finalDataState.state) === null || _b === void 0 ? void 0 : _b.winCase;
    const teams = getTeamsList(winCase);
    const getCards = (team) => (_jsx(Cards, { children: playersInTeam(team).map(({ id, player, isAlive, roleImageUrl, killReason, role, rating, roleName, }, index) => (_jsxs(Card, Object.assign({ backgroundColor: index % 2 === 0
                ? "rgba(0, 0, 0, 0.15);"
                : "rgba(255, 255, 255, 0.02)" }, { children: [_jsx(PlayerStatus, Object.assign({ borderColor: isAlive ? "#87FF81" : "#FF8181" }, { children: _jsx(PlayerPic, { src: player.photoUrl || roleImageUrl || defaultAvatar, alt: "avatar" }) })), _jsx(PlayerName, { children: getName(player) }), killReason && _jsx(KillReason, { children: killReason }), _jsx(PlayerRole, { children: roleName }), _jsx(PlayerRate, { children: rating })] }), id))) }));
    const getBlock = (team, index) => {
        const players = playersInTeam(team);
        if (players.length === 0) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return null;
        }
        return (_jsxs(TeamContainer, { children: [_jsx(TeamTitle, { children: TeamName(team) }), getCards(team)] }, index));
    };
    useEffect(() => {
        if (accessToken && matchId) {
            getFinalData();
        }
    }, [matchId, accessToken]);
    if (!finalDataState) {
        return null;
    }
    return (_jsxs(Section, Object.assign({ backgroundUrl: getBackground(((_c = finalDataState === null || finalDataState === void 0 ? void 0 : finalDataState.state) === null || _c === void 0 ? void 0 : _c.winCase) || "") }, { children: [_jsxs(HeadingContainer, { children: [_jsx(Title, Object.assign({ align: "center", variant: "h2" }, { children: getWinnerString(((_d = finalDataState === null || finalDataState === void 0 ? void 0 : finalDataState.state) === null || _d === void 0 ? void 0 : _d.winCase) || "") })), _jsxs(DateContainer, { children: [finalDataState.state.winCase === TMatchResultCase.WEREWOLF_WIN && (_jsx(RolePostfix, { children: "\u041F\u043E\u0431\u0435\u0434\u0438\u043B" })), _jsx(Date, Object.assign({ align: "center", variant: "h4" }, { children: _jsx("span", { children: (event === null || event === void 0 ? void 0 : event.date) || "" }) }))] }), _jsxs(StatusLegend, { children: [_jsxs(LegendItem, { children: [_jsx(Circle, { circleColor: "#87FF81" }), "\u0412\u044B\u0436\u0438\u043B"] }), _jsxs(LegendItem, { children: [_jsx(Circle, { circleColor: "#FF8181" }), "\u0423\u0431\u0438\u0442"] }), _jsxs(LegendItem, { children: [_jsx(Circle, { circleColor: "#FFC788" }), "\u0420\u0435\u0439\u0442\u0438\u043D\u0433"] })] })] }), _jsx(Teams, { children: teams.map((team, index) => getBlock(team, index)) }), _jsx(BackgroundEffect, {})] })));
}
